import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { GeneralProvider } from './contexts';
import { ErrorPageProvider, useErrorPage } from './contexts/ErrorPageContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import Header from './components/layout/Header/Header';
import Footer from './components/layout/Footer/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import RouteTransition from './components/RouteTransition/RouteTransition';
import Toaster from './components/ui/Toaster/Toaster';
import GlobalLoader from './components/ui/GlobalLoader/GlobalLoader';
import { useTogatherAnalytics } from './hooks/useTogatherAnalytics';
import HomePage from './pages/HomePage/HomePage';
import EventsListPage from './pages/EventsListPage/EventsListPage';
import EventDetailPage from './pages/EventDetailPage/EventDetailPage';
import CommunityPage from './pages/CommunityPage/CommunityPage';
import CommunitiesListPage from './pages/CommunitiesListPage/CommunitiesListPage';
import CreateChannelPage from './pages/CreateChannelPage/CreateChannelPage';
import AdminChannelsPage from './pages/AdminChannelsPage/AdminChannelsPage';
import AuthPage from './pages/Auth/AuthPage';
import { ShareCommunityRedirectPage, ShareEventRedirectPage } from './pages/ShareRedirectPage/ShareRedirectPage';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import OrganizerPanelPage from './pages/OrganizerPanelPage/OrganizerPanelPage';
import OrganizerEventsPage from './pages/OrganizerPanelPage/OrganizerEventsPage/OrganizerEventsPage';
import { CommunitySettingsPage } from './pages/OrganizerPanelPage/CommunitySettingsPage';
import { CommunityMembersPage } from './pages/OrganizerPanelPage/CommunityMembersPage';
import { MailingProcessPage } from './pages/OrganizerPanelPage/MailingProcessPage';
import { CommunityRevenuePage } from './pages/OrganizerPanelPage/CommunityRevenuePage';
import ComingSoon from './components/ui/ComingSoon/ComingSoon';
import EventSettingsPage from './pages/OrganizerPanelPage/EventSettingsPage/EventSettingsPage';
import EventParticipantsPage from './pages/OrganizerPanelPage/EventParticipantsPage/EventParticipantsPage';
import EventParticipantMailingPage from './pages/OrganizerPanelPage/EventParticipantMailingPage/EventParticipantMailingPage';
import EventSpeakersPage from './pages/OrganizerPanelPage/EventSpeakersPage/EventSpeakersPage';
import EventTicketsPage from './pages/OrganizerPanelPage/EventTicketsPage/EventTicketsPage';
import EventSponsorshipsPage from './pages/OrganizerPanelPage/EventSponsorshipsPage/EventSponsorshipsPage';
import SegmentsPage from './pages/OrganizerPanelPage/SegmentsPage/SegmentsPage';
import SegmentUsersPage from './pages/OrganizerPanelPage/SegmentUsersPage/SegmentUsersPage';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import SeoInspector from './components/SEO/SeoInspector';
import './App.css';

const AppContent: React.FC = () => {
  const location = useLocation();
  const { isErrorPage } = useErrorPage();
  const isAuthRoute = location.pathname === '/login' || 
                      location.pathname === '/register' ||
                      location.pathname === '/forgot-password';
  const isAdminRoute = location.pathname.includes('/admin');

  // Track analytics for all pages
  useTogatherAnalytics();

  return (
    <div className="App">
      <ScrollToTop />
      <Toaster />
      <GlobalLoader />
      <SeoInspector />
      {!isAuthRoute && !isErrorPage && <Header />}
      <main className="App-main">
        <RouteTransition>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/share/event/:id" element={<ShareEventRedirectPage />} />
          <Route path="/share/community/:id" element={<ShareCommunityRedirectPage />} />
          
          {/* Protected Routes */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events" 
            element={
              <ProtectedRoute>
                <EventsListPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/communities" 
            element={
              <ProtectedRoute>
                <CommunitiesListPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/communities/create" 
            element={
              <ProtectedRoute>
                <CreateChannelPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/communities/admin" 
            element={
              <ProtectedRoute>
                <AdminChannelsPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Profile Routes */}
          <Route
            path="/profiles/:userPath"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Public Routes - Community and Event Details */}
          <Route 
            path="/events/:eventPath/tickets" 
            element={<EventDetailPage />}
          />
          <Route 
            path="/events/:eventPath/sponsorships" 
            element={<EventDetailPage />}
          />
          <Route 
            path="/events/:eventPath" 
            element={<EventDetailPage />}
          />
          <Route 
            path="/communities/:communityPath" 
            element={<CommunityPage />}
          />
          
          {/* Organizer Panel Routes */}
          <Route 
            path="/communities/:orgName/admin" 
            element={
              <ProtectedRoute>
                <UnsavedChangesProvider>
                  <OrganizerPanelPage />
                </UnsavedChangesProvider>
              </ProtectedRoute>
            }
          >
            <Route 
              index 
              element={
                <ProtectedRoute>
                  <OrganizerEventsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="settings" 
              element={
                <ProtectedRoute>
                  <CommunitySettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="members" 
              element={
                <ProtectedRoute>
                  <CommunityMembersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="mailing" 
              element={
                <ProtectedRoute>
                  <MailingProcessPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="revenue"
              element={
                <ProtectedRoute>
                  <CommunityRevenuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="segments"
              element={
                <ProtectedRoute>
                  <SegmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="segments/:tag"
              element={
                <ProtectedRoute>
                  <SegmentUsersPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Event Admin Routes */}
          <Route 
            path="/communities/:orgName/admin/:eventPath" 
            element={
              <ProtectedRoute>
                <UnsavedChangesProvider>
                  <OrganizerPanelPage />
                </UnsavedChangesProvider>
              </ProtectedRoute>
            }
          >
            <Route 
              index 
              element={
                <ProtectedRoute>
                  <EventSettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="settings" 
              element={
                <ProtectedRoute>
                  <EventSettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="participants" 
              element={
                <ProtectedRoute>
                  <EventParticipantsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="speakers" 
              element={
                <ProtectedRoute>
                  <EventSpeakersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="tickets" 
              element={
                <ProtectedRoute>
                  <EventTicketsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="sponsorships" 
              element={
                <ProtectedRoute>
                  <EventSponsorshipsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="mailing" 
              element={
                <ProtectedRoute>
                  <EventParticipantMailingPage />
                </ProtectedRoute>
              } 
            />
          </Route>
          
          {/* 404 Not Found - catch all route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </RouteTransition>
      </main>
      {!isAuthRoute && !isErrorPage && !isAdminRoute && <Footer />}
    </div>
  );
};

function App() {
  return (
    <Router>
      <GeneralProvider>
        <ErrorPageProvider>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </ErrorPageProvider>
      </GeneralProvider>
    </Router>
  );
}

export default App;
